import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, EnvelopeSimple, MapPin, WhatsappLogo, PaperPlaneTilt, ArrowRight } from "@phosphor-icons/react";
import { Reveal } from "../components/Reveal";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { CONTACT, IMAGES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPICS = [
  "First-time buyer",
  "Moving home",
  "Remortgage",
  "Buy-to-let",
  "Protection",
  "Business protection",
  "Something else",
];

const inputClass =
  "w-full border border-black/10 bg-white px-5 py-3.5 text-sm text-brand-ink placeholder:text-neutral-400 outline-none transition-[border-color,box-shadow] duration-200 focus:border-brand focus:shadow-[0_0_0_3px_rgba(1,86,207,0.12)]";

const Field = ({ label, children }) => (
  <div>
    <label className="mb-2 block text-[0.72rem] font-semibold uppercase tracking-[0.22em] text-brand-ink/70">{label}</label>
    {children}
  </div>
);

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: TOPICS[0], message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSent(true);
      toast.success("Enquiry sent — we'll be in touch shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong — please call us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-brand-bone">
      <Seo
        title="Contact BTK Mortgage Services"
        description="Get in touch with BTK Mortgage Services. Book a call, email or WhatsApp us. Based in Berkshire, serving Berkshire, London, Surrey and the rest of the UK."
        path="/contact"
        image={IMAGES.heroAlt}
      />
      <PageHero
        testId="contact-hero"
        eyebrow="Contact us"
        title="Let's talk about"
        italic="your move."
        intro="Every enquiry lands directly in Ben's inbox — no call centres, no queues. We aim to reply within one working day."
        image={IMAGES.heroAlt}
      >
        <div className="flex flex-wrap items-center gap-6 text-white/80">
          <a href={CONTACT.phoneHref} data-testid="contact-hero-phone" className="group inline-flex items-center gap-3 text-[0.78rem] font-semibold uppercase tracking-[0.22em] text-white transition-colors duration-200 hover:text-brand-gold">
            <Phone size={16} weight="duotone" className="text-brand-gold" />
            {CONTACT.phone}
          </a>
          <span aria-hidden className="hidden sm:inline h-px w-10 bg-white/30" />
          <a href={`mailto:${CONTACT.email}`} data-testid="contact-hero-email" className="group inline-flex items-center gap-3 text-[0.78rem] font-semibold uppercase tracking-[0.22em] text-white transition-colors duration-200 hover:text-brand-gold">
            <EnvelopeSimple size={16} weight="duotone" className="text-brand-gold" />
            {CONTACT.email}
          </a>
        </div>
      </PageHero>

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="eyebrow text-brand"><span className="rule-line" />Reach us</p>
              <h2 className="editorial-h mt-6 text-3xl text-brand-ink sm:text-4xl">
                Four ways to <span className="italic text-brand">get in touch.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <ul className="mt-10 space-y-5">
                <li>
                  <a href={CONTACT.phoneHref} data-testid="contact-phone-link" className="group flex items-center gap-5 border-l-2 border-brand/30 bg-white px-6 py-5 transition-[border-color] duration-200 hover:border-brand">
                    <span className="flex h-11 w-11 items-center justify-center border border-brand/30 text-brand">
                      <Phone size={20} weight="duotone" />
                    </span>
                    <span>
                      <span className="block text-[0.68rem] font-semibold uppercase tracking-[0.3em] text-neutral-500">Call us</span>
                      <span className="editorial-h text-lg text-brand-ink">{CONTACT.phone}</span>
                    </span>
                  </a>
                </li>
                <li>
                  <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="group flex items-center gap-5 border-l-2 border-brand/30 bg-white px-6 py-5 transition-[border-color] duration-200 hover:border-brand">
                    <span className="flex h-11 w-11 items-center justify-center border border-brand/30 text-brand">
                      <EnvelopeSimple size={20} weight="duotone" />
                    </span>
                    <span>
                      <span className="block text-[0.68rem] font-semibold uppercase tracking-[0.3em] text-neutral-500">Email</span>
                      <span className="editorial-h text-lg text-brand-ink">{CONTACT.email}</span>
                    </span>
                  </a>
                </li>
                <li>
                  <a href="https://wa.me/+4401344578847" target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="group flex items-center gap-5 border-l-2 border-brand/30 bg-white px-6 py-5 transition-[border-color] duration-200 hover:border-brand">
                    <span className="flex h-11 w-11 items-center justify-center border border-brand/30 text-brand">
                      <WhatsappLogo size={20} weight="duotone" />
                    </span>
                    <span>
                      <span className="block text-[0.68rem] font-semibold uppercase tracking-[0.3em] text-neutral-500">WhatsApp</span>
                      <span className="editorial-h text-lg text-brand-ink">Message us any time</span>
                    </span>
                  </a>
                </li>
                <li className="flex items-center gap-5 border-l-2 border-brand/30 bg-white px-6 py-5">
                  <span className="flex h-11 w-11 items-center justify-center border border-brand/30 text-brand">
                    <MapPin size={20} weight="duotone" />
                  </span>
                  <span>
                    <span className="block text-[0.68rem] font-semibold uppercase tracking-[0.3em] text-neutral-500">Where we work</span>
                    <span className="editorial-h text-base leading-snug text-brand-ink">Berkshire based — serving Berkshire, London, Surrey &amp; the rest of the UK</span>
                  </span>
                </li>
              </ul>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              {sent ? (
                <div data-testid="contact-success-panel" className="border border-black/10 bg-white p-10 shadow-[0_28px_60px_rgba(10,19,38,0.08)] sm:p-14">
                  <span className="inline-flex h-14 w-14 items-center justify-center border border-brand/30 text-brand">
                    <PaperPlaneTilt size={26} weight="duotone" />
                  </span>
                  <h2 className="editorial-h mt-8 text-3xl text-brand-ink">Thank you — it's on its way</h2>
                  <p className="mt-4 max-w-md text-sm leading-relaxed text-neutral-600">
                    Your enquiry has been sent straight to Ben at {CONTACT.email}. We'll be in touch
                    within one working day. Need us sooner? Call {CONTACT.phone}.
                  </p>
                  <button
                    data-testid="contact-send-another-btn"
                    onClick={() => {
                      setSent(false);
                      setForm({ name: "", email: "", phone: "", topic: TOPICS[0], message: "" });
                    }}
                    className="mt-10 inline-flex items-center gap-2 border border-brand-ink/20 px-6 py-3 text-[0.72rem] font-semibold uppercase tracking-[0.22em] text-brand-ink transition-[border-color,color] duration-200 hover:border-brand hover:text-brand"
                  >
                    Send another enquiry
                    <ArrowRight size={14} weight="bold" />
                  </button>
                </div>
              ) : (
                <form
                  data-testid="contact-form"
                  onSubmit={submit}
                  className="border border-black/10 bg-white p-8 shadow-[0_28px_60px_rgba(10,19,38,0.08)] sm:p-12"
                >
                  <p className="eyebrow text-brand"><span className="rule-line" />Enquiry form</p>
                  <h2 className="editorial-h mt-5 text-3xl text-brand-ink sm:text-4xl">
                    Tell us a <span className="italic text-brand">little</span> about your plans.
                  </h2>
                  <div className="mt-10 grid gap-6 sm:grid-cols-2">
                    <Field label="Your name">
                      <input required minLength={2} data-testid="contact-name-input" className={inputClass} placeholder="Jane Smith" value={form.name} onChange={set("name")} />
                    </Field>
                    <Field label="Email">
                      <input required type="email" data-testid="contact-email-input" className={inputClass} placeholder="jane@example.com" value={form.email} onChange={set("email")} />
                    </Field>
                    <Field label="Phone (optional)">
                      <input type="tel" data-testid="contact-phone-input" className={inputClass} placeholder="07..." value={form.phone} onChange={set("phone")} />
                    </Field>
                    <Field label="What's it about?">
                      <select data-testid="contact-topic-select" className={inputClass} value={form.topic} onChange={set("topic")}>
                        {TOPICS.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <div className="mt-6">
                    <Field label="Your message">
                      <textarea required minLength={10} rows={6} data-testid="contact-message-input" className={`${inputClass} resize-y`} placeholder="Tell us a little about your plans — where you're buying, rough budget, timescales…" value={form.message} onChange={set("message")} />
                    </Field>
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-submit-btn"
                    className="mt-10 inline-flex items-center gap-2 bg-brand px-8 py-4 text-[0.78rem] font-semibold uppercase tracking-[0.22em] text-white shadow-[0_14px_40px_rgba(1,86,207,0.35)] transition-[background-color,transform,opacity] duration-200 hover:bg-brand-dark hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                  >
                    {sending ? "Sending…" : "Send enquiry"}
                    <PaperPlaneTilt size={14} weight="bold" />
                  </button>
                  <p className="mt-6 text-[11px] leading-relaxed text-neutral-400">
                    By submitting this form you agree to us contacting you about your enquiry. See our
                    privacy policy for how we handle your data. We never ask for passwords or card
                    details by email.
                  </p>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
